// A PARTNER-FELÜLET kerete: a shell-adat, a KIVÁLASZTOTT FIÓK és a topbar fejléce — egy contextben.
//
// MIÉRT CONTEXT: a fiók-váltó a sidebarban van, de a választást a LAPOK használják (kezdőlap, jegy-nyitó); a
// topbar címét viszont a lapok tudják (pl. a jegy-szám a morzsában), és a shell rendereli. Ez a két
// „ellentétes irányú" adat a keret sajátja — prop-drillinggel minden lapot át kellene fűzni rajta.
//
// AMI NEM ide tartozik: szerver-állapot. Az `me` egy TanStack Query lekérdezés (`usePortalMe`), a context csak
// TOVÁBBADJA — nincs második cache.
import type { PortalMeResponse } from '@atlas/contracts';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { usePortalScope } from '@/features/portal/use-portal-scope';

/** A topbar fejléce: cím + alcím, VAGY morzsa (a részlet-lapokon) + a lap-test elrendezés-módja. */
export interface PortalHead {
  title?: string | undefined;
  subtitle?: string | undefined;
  crumbs?: readonly { label: string; to?: '/portal/tickets' }[] | undefined;
  /**
   * `true` → a lap-test KITÖLTI a keret magasságát, és a görgetés a lapon BELÜL történik (a `DataTable`
   * saját görgetője). Ez a BELSŐ felület lista-oldalainak geometriája (`flex h-full flex-col px-4 pb-2`):
   * a kártya alja `pb-2`-vel áll meg, vagyis EGY VONALBAN a sidebar aljával, és mivel a keret-`main` nem
   * görget, nincs benne görgetősáv sem — a jobb és a bal margó is egyenlő.
   *
   * Alapból `false`: a lap-test szabadon folyik (kezdőlap, jegy-részlet, profil), és a keret-`main` görget.
   * Ilyenkor a lap ALSÓ térközt kap, hogy az utolsó blokk ne érjen a viewport széléhez.
   */
  fill?: boolean | undefined;
  /**
   * `true` → a MOBIL lebegő „+" gomb (jegy-nyitás) megjelenik ezen a lapon. Alapból `false`.
   *
   * MIÉRT LAP-DÖNTÉS, ÉS MIÉRT NEM A KERET RAKJA KI MINDENHOL (user-lelet): a partner a
   * portálon EGYETLEN dolgot kezdeményezhet — jegyet nyit. A gomb mégis minden lapon ott volt: a
   * jegy-NYITÓN (ahol már ott vagyunk), a kezdőlapon (ahol dedikált „Új jegy" gomb áll), a számláknál, az
   * üzeneteknél (a partner nem ír üzenetet) és a profilon is. Egy akció-gomb, aminek a lapon nincs
   * jelentése, elfedi a tartalmat és megtanítja a felhasználót, hogy ne figyeljen rá.
   *
   * A KERET nem tudja eldönteni, mert a „van-e itt értelme" LAP-tudás; egy shell-beli útvonal-lista pedig
   * minden új lapnál elavulna (és csendben, mert a gomb hibátlanul működne — csak épp rossz helyen).
   */
  newTicketFab?: boolean | undefined;
}

interface PortalContextValue {
  me: PortalMeResponse | undefined;
  /**
   * A FIÓK-SCOPE: a sidebar váltójában kiválasztott fiók, VAGY `null` = **„Minden fiók"** (nincs szűkítés).
   *
   * Ez a felület EGYETLEN scope-mechanizmusa: a lista-lapoknak NINCS saját fiók-szűrője (korábban volt, és a
   * kettő elcsúszott — a váltó globálisnak látszott, de csak egy blokkot szűkített). A `null` az
   * alapértelmezés: aggregált nézet, tehát senki nem veszít adatot véletlenül.
   */
  accountId: string | null;
  setAccountId: (id: string | null) => void;
  head: PortalHead;
  setHead: (head: PortalHead) => void;
}

const PortalContext = createContext<PortalContextValue | null>(null);

export function PortalProvider({
  me,
  scope,
  children,
}: {
  me: PortalMeResponse | undefined;
  /**
   * A scope a provider FÖLÖTT él (`usePortalScope`), mert ő a `/portal/me` kérés paramétere is — itt csak
   * továbbadjuk és ÉRVÉNYESÍTJÜK a beérkezett keret-adat ellen.
   */
  scope?: { accountId: string | null; setAccountId: (id: string | null) => void };
  children: ReactNode;
}): JSX.Element {
  // OPCIONÁLIS scope: aki nem a shellből használja a providert (komponens-teszt, izolált render), annak nem
  // kell felvinnie — ilyenkor a provider SAJÁT példányt tart. A shell mindig átadja a magáét, tehát éles
  // úton EGY példány van (nincs két, egymástól elcsúszó scope-állapot).
  const ownScope = usePortalScope();
  const { accountId, setAccountId } = scope ?? ownScope;
  const [head, setHead] = useState<PortalHead>({});

  // A TÁROLT választás ÉRVÉNYESÍTÉSE: ha a fiók már nem a hívóé (visszavont hozzáférés, régi munkamenet),
  // „Minden fiók"-ra állunk — NEM az első fiókra. Az első fiókra esés csendes, önkényes szűkítés volt: a
  // felhasználó azt hitte, mindent lát, miközben egy fiók adatát látta.
  useEffect(() => {
    if (!me || accountId === null) return;
    if (!me.accounts.some((a) => a.id === accountId)) setAccountId(null);
  }, [me, accountId, setAccountId]);

  const value = useMemo<PortalContextValue>(
    () => ({ me, accountId, setAccountId, head, setHead }),
    [me, accountId, setAccountId, head],
  );
  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

/** A keret adatai. A provideren KÍVÜL hívva dob — így a hiba a fejlesztéskor derül ki, nem élesben. */
export function usePortal(): PortalContextValue {
  const ctx = useContext(PortalContext);
  if (ctx === null) throw new Error('usePortal csak a PortalProvider alatt használható');
  return ctx;
}

/**
 * A TOPBAR FEJLÉCÉNEK beállítása a lapról. A `deps` a primitív értékek listája: a hook csak akkor ír a
 * contextbe, ha ezek változtak — így nincs render-ciklus (a `head` objektum minden renderben új példány).
 */
export function usePortalHead(head: PortalHead, deps: readonly unknown[]): void {
  const { setHead } = usePortal();
  useEffect(() => {
    setHead(head);
    // A `head` SZÁNDÉKOSAN nincs a deps-ben (új objektum minden renderben) — a hívó a primitíveket adja meg.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHead, ...deps]);
}
